import agentSearch from 'locales/en/agentSearch.json';
import common from 'locales/en/common.json';
import expCommercial from 'locales/en/expCommercial.json';
import expInService from 'locales/en/expInService.json';
import expRealtyCa from 'locales/en/expRealtyCa.json';
import expRealtyUs from 'locales/en/expRealtyUs.json';
import formError from 'locales/en/formError.json';
import joinExpCa from 'locales/en/join.exprealtyCA.json';
import joinExpUs from 'locales/en/join.exprealtyUS.json';
import listingExpCommercial from 'locales/en/listing.expCommercial.json';
import modal from 'locales/en/modal.json';
import { LeafPaths } from './LeafPaths';
import { UpperSnake } from './upperSnake';
import { WithTranslation } from 'next-i18next';

//#region Private Constants
const AGENT_SEARCH_NAMESPACE_KEY = 'AGENT_SEARCH' as const;
const COMMON_NAMESPACE_KEY = 'COMMON' as const;
const EXP_COMMERCIAL_NAMESPACE_KEY = 'EXP_COMMERCIAL' as const;
const EXP_IN_SERVICE_NAMESPACE_KEY = 'EXP_IN_SERVICE' as const;
const EXP_REALTY_CA_NAMESPACE_KEY = 'EXP_REALTY_CA' as const;
const EXP_REALTY_US_NAMESPACE_KEY = 'EXP_REALTY_US' as const;
const FORM_ERROR_NAMESPACE_KEY = 'FORM_ERROR' as const;
const JOIN_EXP_CA_NAMESPACE_KEY = 'JOIN_EXP_CA' as const;
const JOIN_EXP_US_NAMESPACE_KEY = 'JOIN_EXP_US' as const;
const MODAL_NAMESPACE_KEY = 'MODAL' as const;
const LISTING_EXP_COMMERCIAL_NAMESPACE_KEY = 'LISTING_EXP_COMMERCIAL' as const;
//#endregion

//#region Public Types
export const AGENT_SEARCH_NAMESPACE = 'agentSearch' as const;
export const COMMON_NAMESPACE = 'common' as const;
export const EXP_COMMERCIAL_NAMESPACE = 'expCommercial' as const;
export const EXP_IN_SERVICE_NAMESPACE = 'expInService' as const;
export const EXP_REALTY_CA_NAMESPACE = 'expRealtyCa' as const;
export const EXP_REALTY_US_NAMESPACE = 'expRealtyUs' as const;
export const FORM_ERROR_NAMESPACE = 'formError' as const;
export const JOIN_EXP_CA_NAMESPACE = 'join.exprealtyCA' as const;
export const JOIN_EXP_US_NAMESPACE = 'join.exprealtyUS' as const;
export const MODAL_NAMESPACE = 'modal' as const;
export const LISTING_EXP_COMMERCIAL_NAMESPACE = 'listing.expCommercial' as const;

export type Namespaces = typeof AGENT_SEARCH_NAMESPACE | typeof COMMON_NAMESPACE | typeof MODAL_NAMESPACE | typeof FORM_ERROR_NAMESPACE | typeof EXP_COMMERCIAL_NAMESPACE | typeof EXP_IN_SERVICE_NAMESPACE | typeof EXP_REALTY_CA_NAMESPACE | typeof EXP_REALTY_US_NAMESPACE | typeof JOIN_EXP_CA_NAMESPACE | typeof JOIN_EXP_US_NAMESPACE | typeof LISTING_EXP_COMMERCIAL_NAMESPACE;

export const Namespaces = {
  [AGENT_SEARCH_NAMESPACE_KEY]: AGENT_SEARCH_NAMESPACE,
  [COMMON_NAMESPACE_KEY]: COMMON_NAMESPACE,
  [MODAL_NAMESPACE_KEY]: MODAL_NAMESPACE,
  [FORM_ERROR_NAMESPACE_KEY]: FORM_ERROR_NAMESPACE,
  [EXP_COMMERCIAL_NAMESPACE_KEY]: EXP_COMMERCIAL_NAMESPACE,
  [EXP_IN_SERVICE_NAMESPACE_KEY]: EXP_IN_SERVICE_NAMESPACE,
  [EXP_REALTY_CA_NAMESPACE_KEY]: EXP_REALTY_CA_NAMESPACE,
  [EXP_REALTY_US_NAMESPACE_KEY]: EXP_REALTY_US_NAMESPACE,
  [JOIN_EXP_CA_NAMESPACE_KEY]: JOIN_EXP_CA_NAMESPACE,
  [JOIN_EXP_US_NAMESPACE_KEY]: JOIN_EXP_US_NAMESPACE,
  [LISTING_EXP_COMMERCIAL_NAMESPACE_KEY]: LISTING_EXP_COMMERCIAL_NAMESPACE,
} as const;

/** A union type of all possible paths in the {@link agentSearch 'locales/en/agentSearch.json'} locale file. */
export type agentSearchPaths = LeafPaths<typeof agentSearch, `${typeof Namespaces.AGENT_SEARCH}:`>;

/** A union type of all possible paths in the {@link common 'locales/en/common.json'} locale file. */
export type commonPaths = LeafPaths<typeof common, `${typeof Namespaces.COMMON}:`>;

/** A union type of all possible paths in the {@link expCommercial 'locales/en/expCommercial.json'} locale file. */
export type expCommercialPaths = LeafPaths<typeof expCommercial, `${typeof Namespaces.EXP_COMMERCIAL}:`>;

/** A union type of all possible paths in the {@link expInService 'locales/en/expInService.json'} locale file. */
export type expInServicePaths = LeafPaths<typeof expInService, `${typeof Namespaces.EXP_IN_SERVICE}:`>;

/** A union type of all possible paths in the {@link expRealtyCa 'locales/en/expRealtyCa.json'} locale file. */
export type expRealtyCaPaths = LeafPaths<typeof expRealtyCa, `${typeof Namespaces.EXP_REALTY_CA}:`>;

/** A union type of all possible paths in the {@link expRealtyUs 'locales/en/expRealtyUs.json'} locale file. */
export type expRealtyUsPaths = LeafPaths<typeof expRealtyUs, `${typeof Namespaces.EXP_REALTY_US}:`>;

/** A union type of all possible paths in the {@link formError 'locales/en/formError.json'} locale file. */
export type formErrorPaths = LeafPaths<typeof formError, `${typeof Namespaces.FORM_ERROR}:`>;

/** A union type of all possible paths in the {@link joinExpCa 'locales/en/join.exprealtyCA.json'} locale file. */
export type joinExpCaPaths = LeafPaths<typeof joinExpCa, `${typeof Namespaces.JOIN_EXP_CA}:`>;

/** A union type of all possible paths in the {@link joinExpUs 'locales/en/join.exprealtyUS.json'} locale file. */
export type joinExpUsPaths = LeafPaths<typeof joinExpUs, `${typeof Namespaces.JOIN_EXP_US}:`>;

/** A union type of all possible paths in the {@link modal 'locales/en/modal.json'} locale file. */
export type modalPaths = LeafPaths<typeof modal, `${typeof Namespaces.MODAL}:`>;

/** A union type of all possible paths in the {@link listingExpCommercial 'locales/en/listing.expCommercial.json'} locale file. */
export type listingExpCommercialPaths = LeafPaths<typeof listingExpCommercial, `${typeof Namespaces.LISTING_EXP_COMMERCIAL}:`>;

/**
 * A union type of all possible paths in the locale files:
 * - {@link agentSearch 'locales/en/agentSearch.json'}
 * - {@link common 'locales/en/common.json'}
 * - {@link expCommercial 'locales/en/expCommercial.json'}
 * - {@link expInService 'locales/en/expInService.json'}
 * - {@link expRealtyCa 'locales/en/expRealtyCa.json'}
 * - {@link expRealtyUs 'locales/en/expRealtyUs.json'}
 * - {@link formError 'locales/en/formError.json'}
 * - {@link joinExpCa 'locales/en/join.exprealtyCA.json'}
 * - {@link joinExpUs 'locales/en/join.exprealtyUS.json'}
 * - {@link modalPaths 'locales/en/modal.json'}
 * - {@link listingExpCommercial 'locales/en/listing.expCommercial.json'}
 */
export type LocalePaths =
  | agentSearchPaths
  | commonPaths
  | expCommercialPaths
  | expInServicePaths
  | expRealtyCaPaths
  | expRealtyUsPaths
  | formErrorPaths
  | joinExpCaPaths
  | joinExpUsPaths
  | modalPaths
  | listingExpCommercialPaths;

/**
 * A type that extends a given type with a {@link WithTranslation} property from next-i18next.
 *
 * This utility type is used to add internationalization capabilities to component props.
 * It combines the provided props type with the WithTranslation interface, which includes
 * properties like `t` (translation function), `i18n` (i18n instance), and `ready` (loading state).
 *
 * @template P - The base props type to extend (defaults to unknown)
 *
 * @example
 * ```tsx
 * type MyComponentProps = PropsWithTranslation<{
 *   title: string;
 *   description: string;
 * }>;
 *
 * const MyComponent = ({ t, title, description }: MyComponentProps) => {
 *   return (
 *     <div>
 *       <h1>{t('common:title')}</h1>
 *       <p>{t('common:description')}</p>
 *     </div>
 *   );
 * };
 *
 * @see {@link WithTranslation} - The next-i18next interface that provides translation capabilities
 */
export type PropsWithTranslation<P = unknown> = P & WithTranslation;

//#endregion

//#region Public Constants
/** An object which maps all possible paths in the {@link agentSearch 'locales/en/agentSearch.json'} locale file. */
export const LOCALE_AGENT_SEARCH_PATHS = makeLocalePathsObject<typeof agentSearch, `${typeof Namespaces.AGENT_SEARCH}`>(agentSearch, Namespaces.AGENT_SEARCH);

/** An object which maps all possible paths in the {@link common 'locales/en/common.json'} locale file. */
export const LOCALE_COMMON_PATHS = makeLocalePathsObject<typeof common, `${typeof Namespaces.COMMON}`>(common, Namespaces.COMMON);

/** An object which maps all possible paths in the {@link expCommercial 'locales/en/expCommercial.json'} locale file. */
export const LOCALE_EXP_COMMERCIAL_PATHS = makeLocalePathsObject<typeof expCommercial, `${typeof Namespaces.EXP_COMMERCIAL}`>(expCommercial, Namespaces.EXP_COMMERCIAL);

/** An object which maps all possible paths in the {@link expInService 'locales/en/expInService.json'} locale file. */
export const LOCALE_EXP_IN_SERVICE_PATHS = makeLocalePathsObject<typeof expInService, `${typeof Namespaces.EXP_IN_SERVICE}`>(expInService, Namespaces.EXP_IN_SERVICE);

/** An object which maps all possible paths in the {@link expRealtyCa 'locales/en/expRealtyCa.json'} locale file. */
export const LOCALE_EXP_REALTY_CA_PATHS = makeLocalePathsObject<typeof expRealtyCa, `${typeof Namespaces.EXP_REALTY_CA}`>(expRealtyCa, Namespaces.EXP_REALTY_CA);

/** An object which maps all possible paths in the {@link expRealtyUs 'locales/en/expRealtyUs.json'} locale file. */
export const LOCALE_EXP_REALTY_US_PATHS = makeLocalePathsObject<typeof expRealtyUs, `${typeof Namespaces.EXP_REALTY_US}`>(expRealtyUs, Namespaces.EXP_REALTY_US);

/** An object which maps all possible paths in the {@link formError 'locales/en/formError.json'} locale file. */
export const LOCALE_FORM_ERROR_PATHS = makeLocalePathsObject<typeof formError, `${typeof Namespaces.FORM_ERROR}`>(formError, Namespaces.FORM_ERROR);

/** An object which maps all possible paths in the {@link joinExpCa 'locales/en/join.exprealtyCA.json'} locale file. */
export const LOCALE_JOIN_EXP_CA_PATHS = makeLocalePathsObject<typeof joinExpCa, `${typeof Namespaces.JOIN_EXP_CA}`>(joinExpCa, Namespaces.JOIN_EXP_CA);

/** An object which maps all possible paths in the {@link joinExpUs 'locales/en/join.exprealtyUS.json'} locale file. */
export const LOCALE_JOIN_EXP_US_PATHS = makeLocalePathsObject<typeof joinExpUs, `${typeof Namespaces.JOIN_EXP_US}`>(joinExpUs, Namespaces.JOIN_EXP_US);

/** An object which maps all possible paths in the {@link modal 'locales/en/modal.json'} locale file. */
export const LOCALE_MODAL_PATHS = makeLocalePathsObject<typeof modal, `${typeof Namespaces.MODAL}`>(modal, Namespaces.MODAL);

/** An object which maps all possible paths in the {@link listingExpCommercial 'locales/en/listing.expCommercial.json'} locale file. */
export const LOCALE_LISTING_EXP_COMMERCIAL_PATHS = makeLocalePathsObject<typeof listingExpCommercial, `${typeof Namespaces.LISTING_EXP_COMMERCIAL}`>(listingExpCommercial, Namespaces.LISTING_EXP_COMMERCIAL);

//#endregion

//#region Private Functions

/**
 * A function which creates an object which maps all possible paths in a locale file to a string.
 *
 * @param obj The object to map.
 * @param prefix The prefix to add to the paths.
 * @returns An object which maps all possible paths in the locale file to a string.
 */
export function makeLocalePathsObject<T extends object, Prefix extends string = ''>(
  obj: T,
  prefix: Prefix
): {[P in LeafPaths<T> as UpperSnake<P>]: `${Prefix}:${P}` } {
  const out = {} as {[P in LeafPaths<T> as UpperSnake<P>]: `${Prefix}:${P}` };

  function recurse(o: object, pfx: string) {
    for (const key of Object.keys(o)) {
      const full = pfx ? `${pfx}.${key}` : key;
      if (o[key] && typeof o[key] === 'object') {
        recurse(o[key], full);
      } else {
        const name = full.toUpperCase().replace(/\./g, '_');
        out[name] = prefix ? `${prefix}:${full}` : full;
      }
    }
  }

  recurse(obj, '');
  return out;
}
//#endregion